import { useMemo } from 'react';

import { useCategoryTreeItem } from '../categoryTree';
import { useInfiniteQueryWithLimit } from '../hooks/useInfiniteQueryWithLimit';
import { queryKeys } from '../queryKeys';
import { composePaginatedData } from '../utils';

import { fetchRentals } from './fetchRentals';
import { RentalAsset } from './types';

type UseRentalsProps = {
  pageSize: number;
  referenceId: string;
};

export function useRentals({ referenceId, pageSize }: UseRentalsProps) {
  const category = useCategoryTreeItem(referenceId);

  const { data, hasNextPage, isLoading, error, fetchNextPage, refetch } = useInfiniteQueryWithLimit<RentalAsset>({
    queryKey: category ? queryKeys.rentals(referenceId, pageSize) : queryKeys.disabled,
    queryFn: ({ pageParam }) => {
      return fetchRentals({
        pageSize,
        pageIndex: pageParam,
        ksql: category?.aggregatedKSQL,
      });
    },
    enabled: Boolean(category),
    pageSize,
  });

  const items = useMemo(() => {
    if (!data) {
      return [];
    }

    return composePaginatedData({ data });
  }, [data]);

  const totalCount = data ? data.pages[0].totalCount : 0;

  return {
    items,
    totalCount,
    hasNextPage,
    isLoading,
    error,
    fetchNextPage,
    refetch,
  };
}
